/**
 * `timeline` — a from/to range chip, and monday's **two-ended date picker**.
 *
 * Captured this round (`discovery/ui-reference/editors/timeline/editor-tier2.json`, the first
 * time this editor was ever opened): the popover is **280 × 373**, radius 4, white; the header
 * row is 264 × 20 with `Set dates` on the left and `N days selected` on the right; under it a
 * 264 × 32 row of **two 128 × 32 date inputs**; then the same `rdp` day grid the `date` picker
 * uses (38 × 30 head cells, a `Apr` 70 × 32 month button and a `2026` 77 × 32 year button, a
 * 64 × 32 month-nav pair).
 */
import {
  getColumnType,
  timelineDays,
  type TimelineSettings,
  type TimelineValue,
} from '@elizmonday/shared';
import { CellPopover, useEditorDraft } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { TimelineSettingsDialog } from './SettingsDialog';

const shared = getColumnType('timeline');

/** Measured: 280 × 373 (`editors/timeline/editor-tier2.json`). */
export const TIMELINE_EDITOR_WIDTH = 280;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const timelineColumnUi = defineColumnUi<TimelineValue, TimelineSettings>({
  type: 'timeline',
  icon: 'columnDate',
  gallery: { group: 'dates', keywords: ['timeline', 'range', 'gantt', 'dates', 'duration'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'center',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value }) => {
    if (value === null) return <span className="ez-cell__empty" aria-hidden="true" />;
    const milestone = value.visualization_type === 'milestone';
    return (
      <span
        className="ez-timeline"
        data-milestone={milestone ? 'true' : 'false'}
        title={`${value.from} - ${value.to}`}
      >
        {`${value.from.slice(5)} – ${value.to.slice(5)}`}
      </span>
    );
  },

  /** **P-282**: the two dates are one draft that follows the store while untouched, and an
   * untouched dismiss writes nothing (`useEditorDraft`). */
  Editor: ({ value, onCommit, onCancel }) => {
    const editor = useEditorDraft({ from: value?.from ?? '', to: value?.to ?? '' });
    const { from, to } = editor.draft;
    const setFrom = (next: string): void => editor.set({ from: next, to });
    const setTo = (next: string): void => editor.set({ from, to: next });
    const valid = DATE_RE.test(from) && DATE_RE.test(to) && from <= to;
    const days = valid ? timelineDays({ from, to }) : 0;
    const commit = (): void => {
      if (from === '' && to === '') {
        onCommit(null);
        return;
      }
      if (!valid) return;
      onCommit({ ...(value ?? {}), from, to });
    };
    return (
      <CellPopover
        label="Set dates"
        width={TIMELINE_EDITOR_WIDTH}
        onDismiss={() => editor.dismiss(commit, onCancel)}
        onCancel={onCancel}
      >
        <div className="ez-timeline-editor">
          <div className="ez-timeline-editor__header">
            <span className="ez-timeline-editor__title">Set dates</span>
            <span className="ez-timeline-editor__count">
              {valid ? `${String(days)} days selected` : ''}
            </span>
          </div>
          <div className="ez-timeline-editor__inputs">
            <input
              className="ez-timeline-editor__input"
              type="date"
              aria-label="Start date"
              data-testid="timeline-from"
              autoFocus
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
            />
            <input
              className="ez-timeline-editor__input"
              type="date"
              aria-label="End date"
              data-testid="timeline-to"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
            />
          </div>
          <button
            type="button"
            className="ez-picker__row ez-picker__row--centered"
            data-testid="timeline-clear"
            onClick={() => onCommit(null)}
          >
            Clear dates
          </button>
        </div>
      </CellPopover>
    );
  },

  SettingsDialog: TimelineSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const [from, to] = trimmed.split(' - ');
    return shared.parseValue({ from, to }, settings) as TimelineValue | null;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('timeline'),
});
